import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';

const ROLE_MODULE_KEY = 'role_management';
const defaultRole = { name: '', description: '', isActive: true };

const cloneModuleTree = (nodes = []) =>
  nodes.map((node) => ({
    ...node,
    permissions: { ...node.permissions },
    subModules: cloneModuleTree(node.subModules || []),
  }));

const collectPermissionPayload = (nodes = []) =>
  nodes.flatMap((node) => [
    { moduleId: node.id, permissions: node.permissions },
    ...collectPermissionPayload(node.subModules || []),
  ]);

const ACTION_LABELS = { allow: 'Allow', view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', approve: 'Approve', export: 'Export' };

const flattenModules = (nodes = []) => nodes.flatMap((node) => [node, ...flattenModules(node.subModules || [])]);

const groupModules = (nodes = []) => {
  const flat = flattenModules(nodes).filter((m) => m.key !== 'settings-roles-permission');
  const byParent = (parentKey) => flat.filter((m) => m.parentKey === parentKey);
  const keysInOrder = (keys) => keys.map((key) => flat.find((m) => m.key === key)).filter(Boolean);
  const mainPageKeys = ['dashboard', 'reports', 'settings'];
  const settingsKeys = [
    'role_management',
    'master',
    'master.companies',
    'settings-bank',
    'settings-user',
    'settings-outlet',
    'settings-designation',
    'settings-shift',
    'settings-document',
    'settings-uniform-type',
  ];
  const supportKeys = ['store_users', 'uniform-forms', 'salary_statement', 'attendance', 'attendance_report', 'attendance_no_punch', 'notifications'];
  return [
    { title: 'Main Pages', items: keysInOrder(mainPageKeys) },
    { title: 'Employee', items: byParent('employee') },
    { title: 'Approvals', items: byParent('approvals') },
    { title: 'Payslip', items: byParent('payslip') },
    { title: 'Reports', items: byParent('reports') },
    { title: 'Dashboard Cards', items: flat.filter((m) => m.type === 'dashboard_card') },
    { title: 'Dashboard Charts', items: flat.filter((m) => m.type === 'dashboard_chart') },
    { title: 'Dashboard Widgets', items: flat.filter((m) => m.type === 'dashboard_widget') },
    { title: 'Settings', items: keysInOrder(settingsKeys) },
    { title: 'Store / Uniform / Salary / Attendance / Notifications', items: flat.filter((m) => supportKeys.includes(m.key)) },
  ].filter((group) => group.items.length);
};

const PermissionRow = ({ module, actions, onToggle, onSelectAllView, onClearModule, onFullAccess }) => {
  const supported = module.supportedActions || actions.map((action) => action.key);
  return (
    <div className="border rounded p-3 mb-2 bg-white">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
        <div>
          <div className="fw-semibold">{module.name}</div>
          <div className="text-muted small">{module.description || module.key}</div>
          <span className="badge bg-light text-dark border mt-1">{module.key}</span>
        </div>
        <div className="btn-group btn-group-sm flex-wrap" role="group">
          <button type="button" className="btn btn-outline-primary" onClick={() => onSelectAllView(module.id)}>Select All View</button>
          <button type="button" className="btn btn-outline-success" onClick={() => onFullAccess(module.id)}>Full Access for this module</button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => onClearModule(module.id)}>Clear Module</button>
        </div>
      </div>
      <div className="d-flex flex-wrap gap-3">
        {actions.filter((action) => supported.includes(action.key)).map((action) => {
          const fieldId = `mod-${module.id}-${action.key}`;
          const checked = Boolean(module.permissions?.[action.key]);
          return (
            <div className="form-check form-check-inline" key={fieldId}>
              <input className="form-check-input" type="checkbox" id={fieldId} checked={checked} onChange={(event) => onToggle(module.id, action.key, event.target.checked)} />
              <label className="form-check-label" htmlFor={fieldId}>{ACTION_LABELS[action.key] || action.label}</label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RoleSettings = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissionMaster, setPermissionMaster] = useState({ modules: [], actions: [] });
  const [moduleSelections, setModuleSelections] = useState([]);
  const [formData, setFormData] = useState(defaultRole);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ type: '', text: '' });
  const permissions = useMemo(() => getModulePermissions(ROLE_MODULE_KEY), []);
  const canView = permissions.view;
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  const canManage = canCreate || canEdit;
  const hasActions = canEdit || canDelete;
  const isViewOnly = canView && !canManage;

  const fetchPermissionMaster = useCallback(async () => {
    setLoadingMaster(true);
    setAlert({ type: '', text: '' });
    try {
      const response = await apiRequest('/api/admin/roles/permission-master', {}, navigate);
      setPermissionMaster({
        modules: cloneModuleTree(response.data?.modules || []),
        actions: response.data?.actions || [],
      });
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to load permissions' });
    } finally {
      setLoadingMaster(false);
    }
  }, [navigate]);

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    setAlert({ type: '', text: '' });
    try {
      const response = await apiRequest('/api/admin/roles', {}, navigate);
      setRoles(response.data || []);
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to load roles.' });
    } finally {
      setLoadingRoles(false);
    }
  }, [navigate]);

  const resetForm = useCallback(() => {
    setSelectedRoleId(null);
    setFormData(defaultRole);
    setModuleSelections(cloneModuleTree(permissionMaster.modules));
  }, [permissionMaster.modules]);

  useEffect(() => {
    if (canView) {
      fetchPermissionMaster();
      fetchRoles();
    }
  }, [canView, fetchPermissionMaster, fetchRoles]);

  useEffect(() => {
    if (!selectedRoleId) {
      setModuleSelections(cloneModuleTree(permissionMaster.modules));
    }
  }, [permissionMaster.modules, selectedRoleId]);

  const togglePermission = (moduleId, actionKey, nextValue) => {
    setModuleSelections((prev) => updateModulePermissions(prev, moduleId, actionKey, nextValue, permissionMaster.actions));
  };

  const handleInputChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const loadRoleDetails = async (roleId) => {
    setSaving(true);
    setAlert({ type: '', text: '' });
    try {
      const response = await apiRequest(`/api/admin/roles/${roleId}`, {}, navigate);
      const role = response.data?.role || {};
      setSelectedRoleId(role.id);
      setFormData({
        name: role.name || '',
        description: role.description || '',
        isActive: role.isActive !== false,
      });
      setModuleSelections(cloneModuleTree(response.data?.modules || []));
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to load role details.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canCreate && !selectedRoleId) {
      setAlert({ type: 'error', text: 'You do not have permission to create roles.' });
      return;
    }

    if (!canEdit && selectedRoleId) {
      setAlert({ type: 'error', text: 'You do not have permission to update roles.' });
      return;
    }

    setSaving(true);
    setAlert({ type: '', text: '' });

    const payload = {
      name: formData.name.trim(),
      description: formData.description?.trim() || '',
      isActive: Boolean(formData.isActive),
      modules: collectPermissionPayload(moduleSelections),
    };

    try {
      const path = selectedRoleId ? `/api/admin/roles/${selectedRoleId}` : '/api/admin/roles';
      const method = selectedRoleId ? 'PUT' : 'POST';
      await apiRequest(path, { method, body: payload }, navigate);
      setAlert({ type: 'success', text: selectedRoleId ? 'Role updated successfully.' : 'Role created successfully.' });
      await fetchRoles();
      if (!selectedRoleId) {
        resetForm();
      }
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to save role.' });
    } finally {
      setSaving(false);
    }
  };

  const handleActivateToggle = async (role) => {
    if (!canEdit) return;
    setSaving(true);
    setAlert({ type: '', text: '' });
    try {
      await apiRequest(`/api/admin/roles/${role.id}`, {
        method: 'PUT',
        body: { isActive: !role.isActive },
      }, navigate);
      await fetchRoles();
      if (selectedRoleId === role.id) {
        loadRoleDetails(role.id);
      }
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to update status.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role, { hard = false } = {}) => {
    if (!canDelete) return;
    const confirmationMessage = hard
      ? `Delete role "${role.name}" permanently? This cannot be undone.`
      : `Deactivate role "${role.name}"? Users will lose access.`;

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    setSaving(true);
    setAlert({ type: '', text: '' });
    try {
      const query = hard ? '?hard=true' : '?mode=deactivate';
      await apiRequest(`/api/admin/roles/${role.id}${query}`, { method: 'DELETE' }, navigate);
      await fetchRoles();
      if (selectedRoleId === role.id) {
        resetForm();
      }
    } catch (error) {
      setAlert({ type: 'error', text: error.message || 'Unable to update role.' });
    } finally {
      setSaving(false);
    }
  };

  const actionsForDisplay = useMemo(() => permissionMaster.actions, [permissionMaster.actions]);
  const permissionGroups = useMemo(() => groupModules(moduleSelections), [moduleSelections]);
  const bulkUpdateModule = (moduleId, mode) => {
    setModuleSelections((prev) => updateModulePermissions(prev, moduleId, mode, true, permissionMaster.actions));
  };

  const roleListContent = useMemo(() => {
    if (loadingRoles) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-4">Loading roles...</td>
        </tr>
      );
    }

    if (alert.type === 'error' && !roles.length) {
      return (
        <tr>
          <td colSpan={4} className="text-center text-danger py-4">{alert.text}</td>
        </tr>
      );
    }

    if (!roles.length) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-4">No roles found.</td>
        </tr>
      );
    }

    return roles.map((role) => (
      <tr key={role.id}>
        <td>
          <div className="fw-semibold">{role.name}</div>
          <div className="text-muted small">{role.description || 'No description provided'}</div>
        </td>
        <td>
          <span className={`badge ${role.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'} border`}>
            {role.isActive ? 'Active' : 'Inactive'}
          </span>
          {role.isSystem && <span className="badge bg-warning-subtle text-warning border ms-2">System</span>}
        </td>
        <td>
          {Array.isArray(role.modulePermissions) && role.modulePermissions.length ? (
            <div className="d-flex flex-wrap gap-2">
              {role.summary?.actions?.length
                ? role.summary.actions.map((action) => (
                    <span key={`${role.id}-${action}`} className="badge bg-light text-dark border">
                      {action}
                    </span>
                  ))
                : <span className="text-muted small">No permissions set</span>}
            </div>
          ) : (
            <span className="text-muted small">No permissions set</span>
          )}
        </td>
        {hasActions && (
          <td className="text-end">
            <div className="btn-group" role="group">
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => loadRoleDetails(role.id)}
                >
                  Edit
                </button>
              )}
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleActivateToggle(role)}
                >
                  {role.isActive ? 'Deactivate' : 'Activate'}
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={role.isSystem}
                  onClick={() => handleDelete(role, { hard: true })}
                >
                  Delete
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    ));
  }, [roles, loadingRoles, alert, canEdit, canDelete, hasActions]);

  if (!canView) {
    return (
      <DashboardLayout>
        <div className="container py-4">
          <div className="alert alert-warning">You do not have access to Role Management.</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-0">Roles &amp; Access</h4>
            <div className="text-muted small">Create, edit, disable, and assign permissions.</div>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => navigate('/settings')}
            >
              Back
            </button>
            {canCreate && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={resetForm}
                disabled={loadingMaster}
              >
                New Role
              </button>
            )}
          </div>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create, edit, and delete actions are disabled.
          </div>
        )}

        {alert.text && (
          <div className={`alert ${alert.type === 'error' ? 'alert-danger' : 'alert-success'}`}>{alert.text}</div>
        )}

        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Existing Roles</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={fetchRoles}
                    disabled={loadingRoles}
                  >
                    Refresh
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Permissions</th>
                        {hasActions && <th className="text-end">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>{roleListContent}</tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            {canManage ? (
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">{selectedRoleId ? 'Edit Role' : 'Create Role'}</h5>
                    {selectedRoleId && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={resetForm}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    )}
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label" htmlFor="role-name">Role Name</label>
                      <input
                        id="role-name"
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label" htmlFor="role-description">Description</label>
                      <textarea
                        id="role-description"
                        className="form-control"
                        rows={2}
                        value={formData.description}
                        onChange={handleInputChange('description')}
                      />
                    </div>

                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="role-active"
                        checked={formData.isActive}
                        onChange={handleInputChange('isActive')}
                      />
                      <label className="form-check-label" htmlFor="role-active">Role is active</label>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Module Permissions</label>
                        {loadingMaster && <span className="text-muted small">Loading modules…</span>}
                      </div>
                      {moduleSelections.length === 0 && (
                        <div className="text-muted small">No modules available.</div>
                      )}
                      {permissionGroups.map((group) => (
                        <div className="mb-4" key={group.title}>
                          <h6 className="text-uppercase text-muted border-bottom pb-2">{group.title}</h6>
                          {group.items.map((module) => (
                            <PermissionRow key={module.id} module={module} actions={actionsForDisplay} onToggle={togglePermission} onSelectAllView={(id) => bulkUpdateModule(id, 'select_view')} onClearModule={(id) => bulkUpdateModule(id, 'clear')} onFullAccess={(id) => bulkUpdateModule(id, 'full')} />
                          ))}
                        </div>
                      ))}
                    </div>

                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : selectedRoleId ? 'Update Role' : 'Create Role'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={resetForm}
                        disabled={saving}
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="alert alert-info h-100" role="alert">
                You can view roles but do not have permission to create or edit them.
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const updateModulePermissions = (nodes, moduleId, actionKey, nextValue, actions = []) => {
  return nodes.map((node) => {
    const updatedNode = { ...node };
    if (node.id === moduleId) {
      const permissions = { ...node.permissions };
      const resolvedValue = nextValue ?? !permissions[actionKey];
      permissions[actionKey] = resolvedValue;

      if (['select_view', 'clear', 'full'].includes(actionKey)) {
        const supported = node.supportedActions || actions.map((action) => action.key);
        actions.forEach((action) => { permissions[action.key] = false; });
        if (actionKey === 'select_view') { permissions.allow = supported.includes('allow'); permissions.view = supported.includes('view'); }
        if (actionKey === 'full') { supported.forEach((key) => { permissions[key] = true; }); }
        updatedNode.permissions = permissions;
        return updatedNode;
      }

      if (actionKey !== 'allow' && resolvedValue) {
        permissions.allow = true;
        permissions.view = true;
      }

      if (actionKey === 'allow' && !resolvedValue) {
        actions.forEach((action) => {
          if (action.key !== 'allow') {
            permissions[action.key] = false;
          }
        });
      }

      updatedNode.permissions = permissions;
    }

    if (Array.isArray(node.subModules) && node.subModules.length) {
      updatedNode.subModules = updateModulePermissions(node.subModules, moduleId, actionKey, nextValue, actions);
    }

    return updatedNode;
  });
};

export default RoleSettings;
